import type { JSX } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api-client';
import { formatNumber } from '@/lib/utils';
import { Select } from '@/components/shared/select';
import type { CostLimit, LimitExemption, Segment, User, ScopeType } from '@echo-work-platform/shared-types';

interface UsageBreakdown {
  totalInputTokens: number;
  totalCachedInputTokens: number;
  totalCacheWriteTokens: number;
  totalOutputTokens: number;
  totalReasoningTokens: number;
  totalTokens: number;
}

interface CostPerModel {
  modelId: string;
  modelName: string;
  inputTokens: number;
  cachedInputTokens: number;
  cacheWriteTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  totalTokens: number;
  totalUsd: number;
}

interface CostRollup {
  scope: string;
  scopeId: string;
  perModel: CostPerModel[];
  totalUsd: number;
}

export function Costs(): JSX.Element {
  const queryClient = useQueryClient();
  const limits = useQuery({
    queryKey: ['/api/admin/cost-limits'],
    queryFn: () => api.get<CostLimit[]>('/api/admin/cost-limits'),
  });
  const segments = useQuery({
    queryKey: ['/api/admin/segments'],
    queryFn: () => api.get<Segment[]>('/api/admin/segments'),
  });
  const users = useQuery({
    queryKey: ['/api/admin/users'],
    queryFn: () => api.get<User[]>('/api/admin/users'),
  });
  const orgUsage = useQuery({
    queryKey: ['/api/admin/usage/aggregate?scope=ORG'],
    queryFn: () => api.get<UsageBreakdown>('/api/admin/usage/aggregate?scope=ORG'),
  });
  const orgCost = useQuery({
    queryKey: ['/api/admin/usage/cost?scope=ORG'],
    queryFn: () => api.get<CostRollup>('/api/admin/usage/cost?scope=ORG'),
  });

  const { register, handleSubmit, reset, control } = useForm<{
    scopeType: ScopeType;
    scopeId: string;
    monthlyBudgetUsd: number;
  }>({ defaultValues: { scopeType: 'ORG' } });
  const scopeType = useWatch({ control, name: 'scopeType' });

  const create = useMutation({
    mutationFn: (v: { scopeType: ScopeType; scopeId?: string; monthlyBudgetUsd: number }) =>
      api.post('/api/admin/cost-limits', v),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['/api/admin/cost-limits'] });
      reset({ scopeType: 'ORG' });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/cost-limits/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/api/admin/cost-limits'] }),
  });

  const scopeOptions = scopeType === 'SEGMENT' ? segments.data : scopeType === 'USER' ? users.data : null;

  // ── Exemptions (unlimited usage) ──────────────────────────────────────────
  const exemptions = useQuery({
    queryKey: ['/api/admin/limit-exemptions'],
    queryFn: () => api.get<LimitExemption[]>('/api/admin/limit-exemptions'),
  });
  const {
    register: registerEx,
    handleSubmit: handleSubmitEx,
    reset: resetEx,
    control: controlEx,
  } = useForm<{ scopeType: 'USER' | 'SEGMENT'; scopeId: string; reason?: string }>({
    defaultValues: { scopeType: 'USER' },
  });
  const exScopeType = useWatch({ control: controlEx, name: 'scopeType' });
  const exScopeOptions = exScopeType === 'SEGMENT' ? segments.data : users.data;
  const createEx = useMutation({
    mutationFn: (v: { scopeType: 'USER' | 'SEGMENT'; scopeId: string; reason?: string }) =>
      api.post('/api/admin/limit-exemptions', v),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['/api/admin/limit-exemptions'] });
      resetEx({ scopeType: 'USER' });
    },
  });
  const removeEx = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/limit-exemptions/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/api/admin/limit-exemptions'] }),
  });

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Costs</h2>

      <div className="card p-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div>
            <p className="text-xs uppercase text-neutral-500">Org havi fogyasztás</p>
            <p className="mt-1 text-2xl font-bold">{formatNumber(orgUsage.data?.totalTokens ?? 0)} token</p>
          </div>
          <div>
            <p className="text-xs uppercase text-neutral-500">Becsült költség</p>
            <p className="mt-1 text-2xl font-bold text-accent-700">
              ${(orgCost.data?.totalUsd ?? 0).toFixed(4)}
            </p>
          </div>
          <div className="col-span-2">
            <p className="text-xs uppercase text-neutral-500">Token bontás</p>
            <p className="mt-1 text-sm font-mono">
              <span title="Input">{formatNumber(orgUsage.data?.totalInputTokens ?? 0)}</span>
              {' / '}
              <span title="Cached input" className="text-emerald-600">
                {formatNumber(orgUsage.data?.totalCachedInputTokens ?? 0)}
              </span>
              {' / '}
              <span title="Cache write" className="text-amber-600">
                {formatNumber(orgUsage.data?.totalCacheWriteTokens ?? 0)}
              </span>
              {' / '}
              <span title="Output">{formatNumber(orgUsage.data?.totalOutputTokens ?? 0)}</span>
              {' / '}
              <span title="Reasoning" className="text-violet-600">
                {formatNumber(orgUsage.data?.totalReasoningTokens ?? 0)}
              </span>
            </p>
            <p className="mt-1 text-[10px] uppercase tracking-wider text-neutral-400">
              input / cached / cache-w / output / reasoning
            </p>
          </div>
        </div>
      </div>

      {orgCost.data && orgCost.data.perModel.length > 0 && (
        <div className="card overflow-hidden">
          <div className="border-b border-neutral-200 px-4 py-2 text-sm font-semibold">
            Költség model-bontásban
          </div>
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-xs uppercase">
              <tr>
                <th className="px-4 py-2 text-left">Model</th>
                <th className="px-4 py-2 text-right">Input</th>
                <th className="px-4 py-2 text-right">Cached</th>
                <th className="px-4 py-2 text-right">Cache w</th>
                <th className="px-4 py-2 text-right">Output</th>
                <th className="px-4 py-2 text-right">Reason</th>
                <th className="px-4 py-2 text-right">Költség (USD)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {orgCost.data.perModel.map((m) => (
                <tr key={m.modelId}>
                  <td className="px-4 py-2 font-medium">{m.modelName}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{formatNumber(m.inputTokens)}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {formatNumber(m.cachedInputTokens)}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {formatNumber(m.cacheWriteTokens)}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{formatNumber(m.outputTokens)}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {formatNumber(m.reasoningTokens)}
                  </td>
                  <td className="px-4 py-2 text-right font-bold">${m.totalUsd.toFixed(4)}</td>
                </tr>
              ))}
              <tr className="bg-neutral-50 font-bold">
                <td className="px-4 py-2 text-right" colSpan={6}>
                  Összesen
                </td>
                <td className="px-4 py-2 text-right text-accent-700">${orgCost.data.totalUsd.toFixed(4)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="card p-4">
        <h3 className="mb-3 text-sm font-semibold">Új cost-limit</h3>
        <form
          className="grid grid-cols-1 gap-3 sm:grid-cols-4"
          onSubmit={(e) =>
            void handleSubmit((v) =>
              create.mutate({
                scopeType: v.scopeType,
                // ORG scope is auto-targeted server-side; no scopeId is sent.
                scopeId: v.scopeType === 'ORG' ? undefined : v.scopeId,
                monthlyBudgetUsd: Number(v.monthlyBudgetUsd),
              }),
            )(e)
          }
        >
          <Controller
            control={control}
            name="scopeType"
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={field.onChange}
                options={[
                  { value: 'ORG', label: 'ORG' },
                  { value: 'SEGMENT', label: 'SEGMENT' },
                  { value: 'USER', label: 'USER' },
                ]}
              />
            )}
          />
          {scopeType !== 'ORG' && (
            <Controller
              control={control}
              name="scopeId"
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  placeholder="— válassz —"
                  options={(scopeOptions ?? []).map((o: { id: string; name?: string; email?: string }) => ({
                    value: o.id,
                    // USER scope is selected by email; SEGMENT by name.
                    label: scopeType === 'USER' ? (o.email ?? o.id) : (o.name ?? o.id),
                  }))}
                />
              )}
            />
          )}
          <input
            className="input"
            type="number"
            step="0.01"
            min="0"
            placeholder="Havi limit (USD)"
            {...register('monthlyBudgetUsd', { required: true })}
          />
          <button className="btn-action" type="submit">
            <Plus className="h-4 w-4" /> Hozzáad
          </button>
        </form>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-xs uppercase">
            <tr>
              <th className="px-4 py-2 text-left">Scope</th>
              <th className="px-4 py-2 text-left">Cél</th>
              <th className="px-4 py-2 text-right">Havi limit</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {limits.data?.map((l) => {
              const targetName =
                l.scopeType === 'SEGMENT'
                  ? segments.data?.find((s) => s.id === l.scopeId)?.name
                  : l.scopeType === 'USER'
                    ? users.data?.find((u) => u.id === l.scopeId)?.email
                    : 'Org';
              return (
                <tr key={l.id}>
                  <td className="px-4 py-2 font-mono text-xs">{l.scopeType}</td>
                  <td className="px-4 py-2">{targetName ?? l.scopeId.slice(0, 8)}</td>
                  <td className="px-4 py-2 text-right">${l.monthlyBudgetUsd.toFixed(2)}</td>
                  <td className="px-4 py-2">
                    <button
                      className="rounded p-1 text-rose-500 hover:bg-rose-50"
                      onClick={() => remove.mutate(l.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {limits.data?.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-neutral-500">
                  Nincs limit.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="card p-4">
        <h3 className="mb-1 text-sm font-semibold">Kivételek — korlátlan használat</h3>
        <p className="mb-3 text-xs text-neutral-500">
          Az itt felsorolt felhasználók és szegmensek mentesülnek MINDEN költségkorlát alól. A mentesítés
          felülír minden más limitet — akkor is, ha a felhasználó egy másik, limitelt szegmensnek is tagja.
        </p>
        <form
          className="grid grid-cols-1 gap-3 sm:grid-cols-4"
          onSubmit={(e) =>
            void handleSubmitEx((v) =>
              createEx.mutate({ scopeType: v.scopeType, scopeId: v.scopeId, reason: v.reason || undefined }),
            )(e)
          }
        >
          <Controller
            control={controlEx}
            name="scopeType"
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={field.onChange}
                options={[
                  { value: 'USER', label: 'USER' },
                  { value: 'SEGMENT', label: 'SEGMENT' },
                ]}
              />
            )}
          />
          <Controller
            control={controlEx}
            name="scopeId"
            rules={{ required: true }}
            render={({ field }) => (
              <Select
                value={field.value ?? ''}
                onChange={field.onChange}
                placeholder="— válassz —"
                options={(exScopeOptions ?? []).map((o: { id: string; name?: string; email?: string }) => ({
                  value: o.id,
                  // USER scope is selected by email; SEGMENT by name.
                  label: exScopeType === 'USER' ? (o.email ?? o.id) : (o.name ?? o.id),
                }))}
              />
            )}
          />
          <input className="input" type="text" placeholder="Indok (opcionális)" {...registerEx('reason')} />
          <button className="btn-action" type="submit">
            <Plus className="h-4 w-4" /> Hozzáad
          </button>
        </form>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-xs uppercase">
            <tr>
              <th className="px-4 py-2 text-left">Scope</th>
              <th className="px-4 py-2 text-left">Cél</th>
              <th className="px-4 py-2 text-left">Indok</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {exemptions.data?.map((e) => {
              const targetName =
                e.scopeType === 'SEGMENT'
                  ? segments.data?.find((s) => s.id === e.scopeId)?.name
                  : users.data?.find((u) => u.id === e.scopeId)?.email;
              return (
                <tr key={e.id}>
                  <td className="px-4 py-2 font-mono text-xs">{e.scopeType}</td>
                  <td className="px-4 py-2">{targetName ?? e.scopeId.slice(0, 8)}</td>
                  <td className="px-4 py-2 text-neutral-500">{e.reason ?? '—'}</td>
                  <td className="px-4 py-2">
                    <button
                      className="rounded p-1 text-rose-500 hover:bg-rose-50"
                      onClick={() => removeEx.mutate(e.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {exemptions.data?.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-neutral-500">
                  Nincs kivétel.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
